import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import subscriptionApi, { PlanInfo, PlanName, UsageStats } from '../services/subscriptionApi';
import { useAuth } from './AuthContext';

interface SubscriptionContextType {
  plan: PlanInfo | null;
  planName: PlanName;
  usage: UsageStats | null;
  isLoading: boolean;
  error: string | null;
  refreshPlan: () => Promise<void>;
  refreshUsage: () => Promise<void>;
  hasFeature: (feature: string) => boolean;
  isWithinLimit: (limitType: string) => Promise<boolean>;
  getRemainingLimit: (limitType: string) => Promise<number>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [plan, setPlan] = useState<PlanInfo | null>(null);
  const [planName, setPlanName] = useState<PlanName>('premium');
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user, isAuthenticated } = useAuth();

  const refreshPlan = async () => {
    if (!isAuthenticated || !user) return;

    setIsLoading(true);
    setError(null);

    try {
      const currentPlan = await subscriptionApi.getCurrentSubscription();
      setPlan(currentPlan);
      setPlanName(currentPlan.plan_name || 'premium');
    } catch (err: any) {
      console.error('Error loading plan:', err);
      // En caso de error, usar el plan del usuario local
      const userPlan = user.subscription_plan || 'premium';
      setPlan(subscriptionApi.getPlanByName(userPlan));
      setPlanName(userPlan);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUsage = async () => {
    if (!isAuthenticated || !user) return;

    try {
      const usageStats = await subscriptionApi.getUsageStats();
      setUsage(usageStats);
    } catch (err: any) {
      console.error('Error loading usage:', err);
    }
  };

  const hasFeature = (feature: string): boolean => {
    if (!plan) {
      // Default a premium features si no hay plan cargado
      return true;
    }
    return plan.features[feature] === true;
  };

  const isWithinLimit = async (limitType: string): Promise<boolean> => {
    try {
      const limitCheck = await subscriptionApi.checkLimit(limitType);
      return limitCheck.allowed;
    } catch (err) {
      console.error('Error checking limit:', err);
      return true; // En caso de error, permitir
    }
  };

  const getRemainingLimit = async (limitType: string): Promise<number> => {
    try {
      const limitCheck = await subscriptionApi.checkLimit(limitType);
      return limitCheck.remaining;
    } catch (err) {
      console.error('Error getting remaining limit:', err);
      return -1; // -1 = ilimitado
    }
  };

  // Cargar plan cuando el usuario esté autenticado
  useEffect(() => {
    if (isAuthenticated && user) {
      refreshPlan();
      refreshUsage();
    }
  }, [isAuthenticated, user]);

  return (
    <SubscriptionContext.Provider
      value={{
        plan,
        planName,
        usage,
        isLoading,
        error,
        refreshPlan,
        refreshUsage,
        hasFeature,
        isWithinLimit,
        getRemainingLimit
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription debe ser usado dentro de un SubscriptionProvider');
  }
  return context;
};
